import React, { useState } from "react";
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, Spinner, FormGroup, Label, Input, FormFeedback, Row, Col,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { adminUpdateCompanySubscriptionSettings } from "../../../api/requests/subscription-requests";
import DateInput from "../../../components/Common/DateInput";

const NAVY = "#221C5D";

/**
 * Firma abonelik ayarları düzenleme modalı (SUPER_ADMIN)
 * - Yenileme fiyatı (custom renewal price)
 * - Maks. cari hesap sayısı
 * - Maks. kullanıcı sayısı
 *
 * @param {boolean}  isOpen
 * @param {Function} toggle
 * @param {Object}   subscription  - tablo satırındaki sub nesnesi
 * @param {Function} onSuccess
 */
const EditSubscriptionSettingsModal = ({ isOpen, toggle, subscription, onSuccess }) => {
    const [saving, setSaving] = useState(false);

    const sub = subscription || {};

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            maxAccounts: sub.maxAccounts ?? "",
            maxUsers: sub.maxUsers ?? "",
            allowCustomDomain: sub.allowCustomDomain ?? false,
            endDate: sub.endDate ? sub.endDate.slice(0, 10) : "",
        },
        validationSchema: Yup.object({
            maxAccounts: Yup.number()
                .integer("Tam sayı giriniz")
                .min(1, "En az 1 olmalı")
                .nullable(),
            maxUsers: Yup.number()
                .integer("Tam sayı giriniz")
                .min(1, "En az 1 olmalı")
                .nullable(),
            endDate: Yup.date().nullable(),
        }),
        onSubmit: async (values) => {
            if (!sub.companyId) return;
            setSaving(true);
            try {
                await adminUpdateCompanySubscriptionSettings({
                    companyId: sub.companyId,
                    subscriptionId: sub.id,
                    maxAccounts: values.maxAccounts !== "" ? Number(values.maxAccounts) : null,
                    maxUsers: values.maxUsers !== "" ? Number(values.maxUsers) : null,
                    allowCustomDomain: values.allowCustomDomain,
                    endDate: values.endDate || null,
                });
                toast.success("Abonelik ayarları güncellendi");
                handleClose();
                if (onSuccess) onSuccess();
            } catch (err) {
                toast.error(err.message);
            } finally {
                setSaving(false);
            }
        },
    });

    const handleClose = () => {
        formik.resetForm();
        toggle();
    };

    if (!subscription) return null;

    const statusColor =
        sub.status === "ACTIVE" ? "#71C30B" :
        sub.status === "TRIAL" ? "#E56A22" :
        sub.status === "EXPIRED" ? "#B0B7C3" :
        sub.status === "CANCELLED" ? "#E07A7A" : "#221C5D";

    const statusLabel =
        sub.status === "ACTIVE" ? "Aktif" :
        sub.status === "TRIAL" ? "Deneme" :
        sub.status === "EXPIRED" ? "Süresi Dolmuş" :
        sub.status === "CANCELLED" ? "İptal" :
        sub.status === "UPGRADED" ? "Yükseltilmiş" :
        sub.status === "SCHEDULED" ? "Planlandı" : sub.status;

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered>
            <ModalHeader toggle={handleClose} className="border-bottom">
                <div className="d-flex align-items-center gap-2" style={{ color: NAVY }}>
                    <i className="bx bx-cog fs-4" />
                    Abonelik Ayarları
                </div>
            </ModalHeader>

            <ModalBody className="py-4">
                {/* Firma bilgisi */}
                <div className="border rounded p-3 bg-light mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <i className="bx bx-building-house text-muted" style={{ fontSize: 20 }} />
                        <div className="flex-grow-1">
                            <div className="fw-semibold text-dark">{sub.companyName}</div>
                            <div className="d-flex align-items-center gap-2 mt-1">
                                <span className="text-muted small">{sub.planName}</span>
                                <span
                                    className="badge border-0"
                                    style={{ borderRadius: 999, backgroundColor: statusColor, color: "#fff", fontSize: ".7rem" }}
                                >
                                    {statusLabel}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex gap-3 mt-2 text-muted small">
                        <span><i className="bx bx-group me-1" />{sub.currentAccounts}/{sub.maxAccounts || "∞"} cari</span>
                        <span><i className="bx bx-user me-1" />{sub.currentUsers}/{sub.maxUsers || "∞"} kullanıcı</span>
                    </div>
                </div>

                {/* Bitiş Tarihi */}
                <div className="mb-3 p-3 border rounded">
                    <h6 className="fw-semibold mb-2 d-flex align-items-center gap-2">
                        <i className="bx bx-calendar text-muted" />
                        Abonelik Bitiş Tarihi
                    </h6>
                    <p className="text-muted small mb-2">
                        Aboneliğin bitiş tarihini değiştirebilirsiniz. Süre uzatma veya kısaltma için kullanılır.
                    </p>
                    <FormGroup className="mb-0">
                        <DateInput
                            value={formik.values.endDate}
                            onChange={(iso) => formik.setFieldValue("endDate", iso)}
                        />
                        {formik.touched.endDate && formik.errors.endDate && (
                            <div className="text-danger small mt-1">{formik.errors.endDate}</div>
                        )}
                    </FormGroup>
                </div>

                {/* "Yenileme Fiyatı" alanı KALDIRILDI: yazdığı renewal_price_override kolonu
                    hiçbir fiyat hesabına girmiyordu (PriceCalculationService yalnız
                    PriceOverride tablosuna bakar). Alan doluyken bile firma liste fiyatından
                    ödüyordu; indirim verildiği sanılan sessiz bir tuzaktı. Firmaya özel fiyat
                    Fiyat Müdahaleleri ekranından tanımlanır. */}
                <div className="mb-3 p-3 border rounded bg-light">
                    <h6 className="fw-semibold mb-2 d-flex align-items-center gap-2">
                        <i className="bx bx-money text-muted" />
                        Firmaya özel fiyat
                    </h6>
                    <p className="text-muted small mb-0">
                        Özel fiyat bu ekrandan değil, <strong>Fiyat Müdahaleleri</strong> ekranından
                        tanımlanır; satın alma ve yenilemede uygulanan fiyat oradan gelir.
                    </p>
                </div>

                {/* Hak Limitleri */}
                <div className="p-3 border rounded">
                    <h6 className="fw-semibold mb-2 d-flex align-items-center gap-2">
                        <i className="bx bx-shield-quarter text-muted" />
                        Abonelik Hakları
                    </h6>
                    <p className="text-muted small mb-2">
                        Plan limitlerinden bağımsız olarak bu firmaya özel haklar tanımlayabilirsiniz.
                    </p>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label className="small fw-medium">Maks. Cari Hesap</Label>
                                <Input
                                    type="number"
                                    name="maxAccounts"
                                    value={formik.values.maxAccounts}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={formik.touched.maxAccounts && !!formik.errors.maxAccounts}
                                    placeholder={sub.maxAccounts || "Limitsiz"}
                                />
                                <FormFeedback>{formik.errors.maxAccounts}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label className="small fw-medium">Maks. Kullanıcı</Label>
                                <Input
                                    type="number"
                                    name="maxUsers"
                                    value={formik.values.maxUsers}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={formik.touched.maxUsers && !!formik.errors.maxUsers}
                                    placeholder={sub.maxUsers || "Limitsiz"}
                                />
                                <FormFeedback>{formik.errors.maxUsers}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                    <div className="form-check mt-2">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="allowCustomDomain"
                            name="allowCustomDomain"
                            checked={formik.values.allowCustomDomain}
                            onChange={formik.handleChange}
                        />
                        <label className="form-check-label small fw-medium" htmlFor="allowCustomDomain">
                            Özel Domain Kullanımı
                        </label>
                        <div className="text-muted" style={{ fontSize: ".72rem" }}>
                            Firma kendi domain'ini bağlayabilir
                        </div>
                    </div>
                </div>
            </ModalBody>

            <ModalFooter className="border-top">
                <div className="d-flex justify-content-end gap-2 w-100">
                    <Button color="secondary" onClick={handleClose} disabled={saving} className="px-4">
                        İptal
                    </Button>
                    <Button
                        style={{ backgroundColor: NAVY, borderColor: NAVY }}
                        onClick={formik.handleSubmit}
                        disabled={saving}
                        className="px-4 d-flex align-items-center gap-2"
                    >
                        {saving ? (
                            <><Spinner size="sm" /> Kaydediliyor...</>
                        ) : (
                            <><i className="bx bx-check" /> Kaydet</>
                        )}
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default EditSubscriptionSettingsModal;
